import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import api from '@/services/axios'
import type { Business, Order, Stats, Category } from '@/types'
export const useBusinessStore = defineStore('business', () => {
  const recentOrders = ref<Order[]>([])
  const businesses = ref<Business[]>([])
  const business = ref<Business | null>(null)
  const businessCategories = ref<Category[]>([])
  const isLoadingBusinesses = ref(false)
  const stats = ref<Stats>({})
  const availabilityError = ref<string | null>(null);
  const pagination = ref({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: 0,
    to: 0,
    links: []
  })
  const currentPage = ref(1)
  // Lazy-loaded queries
  const businessDashboardDataQuery = useQuery({
    queryKey: ['businessDashboardData'],
    queryFn: async () => {

    },
    enabled: false, // Don't run automatically
  })

  const fetchBusinessCategories = async (business_id: string) => {
    try {
      const response = await api.get(`/api/products/categories/${business_id}`)
      businessCategories.value = response.data
      return response.data
    } catch (error) {
      console.error('Error fetching business categories:', error)
      throw error
    }
  }

  // Fetch businesses with filters and pagination
const fetchBusinesses = async (filters: any = {}, page = 1, per_page = 10) => {
  try {
    isLoadingBusinesses.value = true
    const params: Record<string, any> = { ...filters, page, per_page };
    const response = await api.get('api/businesses', { params });
    businesses.value = response.data.data;
    pagination.value = {
      current_page: response.data.current_page,
      last_page: response.data.last_page,
      per_page: response.data.per_page,
      total: response.data.total,
      from: response.data.from,
      to: response.data.to,
      links: response.data.links
    };
    return response.data;
  } catch (error) {
    console.error('Error fetching businesses:', error);
    throw error;
  } finally {
    isLoadingBusinesses.value = false
  }
}

  const fetchSingleBusiness = async (id: string, filters: any = {search: '', category: '', subcategory: ''}) => {
    try {
      const response = await api.get(`/api/businesses/${id}?search=${filters.search}&category=${filters.category}&subcategory=${filters.subcategory}`)
      business.value = response.data
      return response.data
    } catch (error) {
      console.error('Error fetching business:', error)
      throw error
    }
  }

  async function toggleAvailability() {
    availabilityError.value = null

    try {
      // Fix the URL to match the exact route defined in the backend
      const response = await api.put('/api/business/toggle-availability')
      stats.value.is_active = response.data.isAvailable
      return response.data.isAvailable
    } catch (err: any) {
      availabilityError.value = err.response?.data?.message || 'Failed to update availability'
      console.error('Error toggling availability:', err)
      throw err
    }
  }

  // Query for fetching businesses
  // const businessesQuery = useQuery({
  //   queryKey: ['businesses'],
  //   queryFn: async () => {
  //     const response = await api.get('/api/customer/businesses')
  //     return response.data
  //   },
  //   onSuccess: (data) => {
  //     businesses.value = data
  //   },
  //   refetchOnWindowFocus: false,
  // })

  // Function to manually refresh data
  const fetchDashboardData = async(dateFilter = 'today', startDate = null, endDate = null) => {
    try {
      const response = await api.get(`api/business/dashboard?date_filter=${dateFilter}&start_date=${startDate}&end_date=${endDate}`)
      recentOrders.value = response.data.recentOrders
      business.value = response.data.business
      stats.value = response.data.stats
      return response.data
    } catch (error) {
      console.error('Error fetching business dashboard data:', error)
      throw error
    }
    businessDashboardDataQuery.refetch()
    // businessesQuery.refetch()
  }

  return {
    recentOrders,
    business,
    stats,
    toggleAvailability,
    businesses,
    businessDashboardDataQuery,
    fetchSingleBusiness,
    fetchDashboardData,
    fetchBusinesses,
    fetchBusinessCategories,
    businessCategories,
    pagination,
    currentPage,
    isLoadingBusinesses,
    isLoading: computed(() => businessDashboardDataQuery.isLoading.value),
    isError: computed(() => businessDashboardDataQuery.isError.value),
    availabilityError,
  }
})
